import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/* Scroll reveal: elements with .hg-reveal fade up once they enter the viewport. */
function initReveal() {
  const items = document.querySelectorAll('.hg-reveal:not(.is-visible)');
  if (!items.length) return;

  if (!('IntersectionObserver' in window)) {
    items.forEach((el) => el.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
  );

  items.forEach((el) => observer.observe(el));
}

function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);
}

async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
  return response.json();
}

/* "+ Dazulegen" buttons: add one variant and let the theme's cart drawer react like a normal add to cart. */
async function addToCart(button) {
  const variantId = Number(button.dataset.variantId);
  if (!variantId || button.getAttribute('aria-busy') === 'true') return;

  button.setAttribute('aria-busy', 'true');
  const deferred = CartLinesUpdateEvent.createPromise();

  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: cartSectionIds().join(',') }),
    });
    const result = await response.json();
    const cart = await fetchCart();

    if (result.status) {
      button.dispatchEvent(new CartErrorEvent({ error: result.message || 'Add to cart failed', code: 'INVALID' }));
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, items: cart.items, source: 'hg-quick-add' },
      });
      return;
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'hg-quick-add',
        itemCount: 1,
        sections: result.sections,
        didError: false,
      },
    });
    button.classList.add('is-added');
  } catch (error) {
    console.error('[hg-quick-add]', error);
    deferred.reject(error);
    button.dispatchEvent(new CartErrorEvent({ error: 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' }));
  } finally {
    button.removeAttribute('aria-busy');
  }
}

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-hg-add]') : null;
  if (!button) return;
  event.preventDefault();
  addToCart(button);
});

window.hgRevealReady = true;
initReveal();
document.addEventListener('shopify:section:load', initReveal);
